/* eslint-disable react-hooks/rules-of-hooks -- Upstream attaches TanStack Query hooks to result objects as .use/.useSuspense; callers invoke them from components. */
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import type {
  InfiniteData,
  QueryClient,
  UseInfiniteQueryOptions,
  UseSuspenseInfiniteQueryOptions,
} from '@tanstack/react-query';

import type { z } from 'zod/v4';

import type {
  AbstractEndpoint,
  AnyEndpointConfig,
  UrlParams,
} from '../../index.js';
import { createQueryKey } from './key-creator.js';
import type { InfiniteQueryOptions, QueryArgs } from './types.js';

/**
 * Creates infinite query options for a given endpoint.
 *
 * Returns a function that generates TanStack Query infinite options when called with params.
 * The returned function also has helper methods attached (use, useSuspense, invalidate, etc.)
 *
 * @param endpoint - The navios endpoint to create infinite query options for
 * @param options - Infinite query configuration including processResponse and pagination params
 * @param baseQuery - Optional base query options to merge
 * @returns A function that generates infinite query options with attached helpers
 */
export function makeInfiniteQueryOptions<
  Config extends AnyEndpointConfig,
  Options extends InfiniteQueryOptions<Config>,
  BaseQuery extends Omit<
    UseInfiniteQueryOptions<ReturnType<Options['processResponse']>, Error, any>,
    | 'queryKey'
    | 'queryFn'
    | 'getNextPageParam'
    | 'initialPageParam'
    | 'placeholderData'
    | 'throwOnError'
  >,
>(
  endpoint: AbstractEndpoint<Config>,
  options: Options,
  baseQuery: BaseQuery = {} as BaseQuery,
) {
  const config = endpoint.config;
  const queryKey = createQueryKey(config, options, true);

  const processResponse = options.processResponse;
  const res = (
    params: QueryArgs<Config['url'], Config['querySchema']>,
  ): Options['processResponse'] extends (...args: any[]) => infer Result
    ? UseSuspenseInfiniteQueryOptions<
        Result,
        Error,
        BaseQuery['select'] extends (...args: any[]) => infer T
          ? T
          : InfiniteData<Result>
      >
    : never => {
    // @ts-expect-error TS2322 We know that the processResponse is defined
    return infiniteQueryOptions({
      // @ts-expect-error TS2345 We bind the url params only if the url has params
      queryKey: queryKey.dataTag(params),
      queryFn: async ({
        signal,
        pageParam,
      }): Promise<ReturnType<Options['processResponse']>> => {
        let result;
        try {
          result = await endpoint({
            signal,
            // @ts-expect-error TS2345 We bind the url params only if the url has params
            urlParams: params.urlParams as z.infer<UrlParams<Config['url']>>,
            params: {
              ...('params' in params ? params.params : {}),
              ...(pageParam as z.infer<Config['querySchema']>),
            },
          });
        } catch (err) {
          if (options.onFail) {
            options.onFail(err);
          }
          throw err;
        }

        return processResponse(result) as ReturnType<
          Options['processResponse']
        >;
      },
      getNextPageParam: options.getNextPageParam,
      getPreviousPageParam: options.getPreviousPageParam,
      initialPageParam:
        options.initialPageParam ??
        config.querySchema?.parse('params' in params ? params.params : {}) ??
        ('params' in params ? params.params : {}),
      ...baseQuery,
    });
  };
  /** The query key creator for this infinite query endpoint */
  res.queryKey = queryKey;

  /**
   * React hook that executes the infinite query.
   * Uses `useInfiniteQuery` from TanStack Query internally.
   *
   * @param params - URL parameters and initial query parameters
   * @returns Infinite query result with pages, fetchNextPage, etc.
   */
  res.use = (params: QueryArgs<Config['url'], Config['querySchema']>) => {
    return useInfiniteQuery(res(params));
  };

  /**
   * React hook that executes the infinite query with Suspense support.
   * Uses `useSuspenseInfiniteQuery` from TanStack Query internally.
   * The component will suspend while loading and throw on error.
   *
   * @param params - URL parameters and initial query parameters
   * @returns Infinite query result with pages guaranteed to be defined
   */
  res.useSuspense = (
    params: QueryArgs<Config['url'], Config['querySchema']>,
  ) => {
    return useSuspenseInfiniteQuery(res(params));
  };

  /**
   * Invalidates this specific infinite query in the cache.
   *
   * @param queryClient - The TanStack Query client instance
   * @param params - The exact parameters used for this query
   * @returns A promise that resolves when invalidation completes
   */
  res.invalidate = (
    queryClient: QueryClient,
    params: QueryArgs<Config['url'], Config['querySchema']>,
  ) => {
    return queryClient.invalidateQueries({
      // @ts-expect-error TS2345 We bind the url params only if the url has params
      queryKey: res.queryKey.dataTag(params),
    });
  };

  /**
   * Invalidates all infinite queries matching the URL pattern.
   * Useful for invalidating all queries for a resource regardless of query params.
   *
   * @param queryClient - The TanStack Query client instance
   * @param params - URL parameters only (query params are ignored for matching)
   * @returns A promise that resolves when invalidation completes
   */
  res.invalidateAll = (
    queryClient: QueryClient,
    params: QueryArgs<Config['url'], Config['querySchema']>,
  ) => {
    return queryClient.invalidateQueries({
      // @ts-expect-error TS2345 We bind the url params only if the url has params
      queryKey: res.queryKey.filterKey(params),
      exact: false,
    });
  };

  return res;
}
